import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    signal,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    WritableSignal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HectometreService } from 'app/services/hectometre/hectometre.service';
import { EMPTY, merge, Observable } from 'rxjs';
import { auditTime, debounceTime, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Customer } from '../customer';
import {
    ExportDialogData,
    CustomerProjectExportDialogComponent,
} from '../customer-project-detail/customer-project-export-dialog/customer-project-export-dialog.component';
import { CustomerType } from '../customer-type.enum';
import { Filter } from '../filter';
import { GoogleMapsComponent } from '../google-maps/google-maps.component';
import { LocalStorage } from '../local-storage.class';
import { WorkareaTransferDialogComponent } from '../map-measurements/workarea-transfer-dialog/workarea-transfer-dialog.component';
import { WorkareasBoundsCountPipe } from '../pipes/workareas-bounds-count.pipe';
import { Region } from '../region.class';
import { Features } from '../services/auth/features';
import { Functionalities } from '../services/auth/functionalities';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { ExportService } from '../services/export/export.service';
import { WorkareaService } from '../services/workarea/workarea.service';
import { Settings } from '../settings.class';
import { Utils } from '../utils.class';
import { Workarea } from '../workarea.class';
import { AddressSearchResult, LocationService } from '../workarea/shared/location.service';
import { WorkareasFilterComponent } from '../workareas-filter/workareas-filter.component';
import { WorkareaProjectChooseDialogComponent } from './workarea-type-choose-dialog/workarea-project-choose-dialog.component';

@Component({
    selector: 'app-workareas-map',
    templateUrl: './workareas-map.component.html',
    styleUrls: ['./workareas-map.component.scss'],
    standalone: false,
})
export class WorkareasMapComponent extends GoogleMapsComponent implements OnInit, AfterViewInit, OnDestroy {
    private overlayRef: OverlayRef | null = null;

    public customer: Customer = {} as Customer;
    readonly Features = Features;
    readonly Functionalities = Functionalities;
    readonly CustomerType = CustomerType;

    measuring: boolean = false;

    polygonOptions = {
        clickable: false,
        strokeColor: '#0F0064',
        fillColor: '#85D1E4',
        fillOpacity: 0.35,
        strokeWeight: 2,
    };
    doNotClusterBelowPointsInView = 100;

    public Settings = Settings;
    public selectedWorkareas: Workarea[] = [];
    public measurementSelectedWorkareas: Workarea[] = [];
    public selectedPosition: any = {};
    public searchbarVisible = false;
    public filterbarVisible = false;
    public filterbarWasVisible = false;
    public mobileMenuOpen = false;
    public Utils = Utils;
    public loading = false;
    public searchResults: any[] = [];
    public addressResults: WritableSignal<AddressSearchResult[]> = signal([]);
    public workareas: WritableSignal<Workarea[]> = signal([]);
    public filteredWorkareas: WritableSignal<Workarea[]> = signal([]);
    public totalWorkareaCount = 0;
    public filteredWorkareaCount = 0;
    public appliedAnyFilter = false;
    public mapBounds: google.maps.LatLngBounds | null = null;
    public renderBounds: google.maps.LatLngBounds | null = null;
    private renderTriggerBounds: google.maps.LatLngBounds | null = null;
    private renderZoom: number | null = null;
    public workareasInBoundsCount = 0;
    public readonly markerOptions: google.maps.MarkerOptions = { clickable: false };
    private readonly workareasBoundsCountPipe = new WorkareasBoundsCountPipe();
    public hectometers: any[] = [];
    public hectoPins: WritableSignal<any[]> = signal([]);
    // Buffered bounds prevent refetching on small map moves.
    private hectometersBounds: google.maps.LatLngBounds | null = null;
    private hectometersDetailed = false;
    screenshot = false;
    projectId;
    customerRegion: Region;
    public actualZoom = 0;
    @ViewChild('mapFilter', { static: true }) private filterRef: WorkareasFilterComponent;
    private scrollToWorkareaId: number;
    private filter = new Filter();
    addressAutocomplete = new FormControl('');
    addressSearch;
    private lastHectoRoad: string | null = null;
    movingWorkarea: number;
    isSearching: boolean;
    status: any[] = [];
    hideClusterPolygons = false;
    pdfStyle: any;

    constructor(
        private workareaService: WorkareaService,
        private router: Router,
        private ngZone: NgZone,
        private route: ActivatedRoute,
        private dialog: MatDialog,
        private activatedRoute: ActivatedRoute,
        private locationService: LocationService,
        private overlay: Overlay,
        private vcr: ViewContainerRef,
        private hectoService: HectometreService,
        private customerProjectService: CustomerProjectService,
        private exportService: ExportService,
        protected confirmDialogService: ConfirmDialogService,
        protected cdref: ChangeDetectorRef
    ) {
        super(confirmDialogService, cdref);

        let viewing = '';
        this.subscriptions.add(
            this.router.events.subscribe(event => {
                if (event instanceof NavigationEnd) {
                    const currentView = `${this.customer.id}-${this.route.snapshot.queryParams['project']}`;
                    setTimeout(() => this.reload(viewing != currentView));
                    viewing = currentView;
                }
            })
        );
    }

    private updateWorkareaSearch(value: string): void {
        this.searchResults = [];
        if (!value || !this.filteredWorkareas()?.length) {
            return;
        }

        const normalizedValue = value.toLowerCase();
        this.filteredWorkareas().forEach(workarea => {
            if (
                workarea.number.toString().indexOf(value) !== -1 ||
                (workarea.street ?? '').toLowerCase().indexOf(normalizedValue) !== -1 ||
                (workarea.comments ?? '').toLowerCase().indexOf(normalizedValue) !== -1
            ) {
                this.searchResults.push(workarea);
            }
        });
    }

    private isHectoSearch(value: string): boolean {
        return /^([an][0-9]+)(([ ,]{1,3})([0-9]{1,4}[,. ]?[0-9]?))?/i.test(value);
    }

    private createHectoSearchStream(search$: Observable<string>): Observable<any[]> {
        return search$.pipe(
            filter((value: string) => !!value && this.isHectoSearch(value)),
            switchMap((value: string) =>
                this.hectoService.search(value).pipe(
                    map(result => {
                        const waynumber = result.data[0]?.waynumber;

                        if (waynumber !== this.lastHectoRoad) {
                            this.lastHectoRoad = waynumber;
                        }

                        this.hectoPins.set(result.data);
                        return [];
                    })
                )
            )
        );
    }

    private createAddressSearchStream(search$: Observable<string>): Observable<AddressSearchResult[]> {
        return this.locationService.searchAddresses(
            search$.pipe(
                filter((value: string) => !!value && !this.isHectoSearch(value)),
                tap(() => this.hectoPins.set([]))
            ),
            'WorkareasMapComponent.addressSearch'
        );
    }

    public searchToggle() {
        if (this.mobileMenuOpen) {
            this.mobileMenuOpen = false;
            this.mobileMenuOpen = false;
        }
        this.searchbarVisible = !this.searchbarVisible;
    }

    goToAddress(result) {
        const coords = result;
        this.setMapCenterAndZoom({ lat: +coords['lat'], lng: +coords['lng'] });
        this.isSearching = false;
    }

    onMeasurementSelectionChange(selectedWorkareas: Workarea[]): void {
        this.measurementSelectedWorkareas = selectedWorkareas ?? [];
    }

    openMeasurementTransferDialog(): void {
        const dialogRef = this.dialog.open(WorkareaTransferDialogComponent, {
            maxHeight: '98vh',
            width: '500px',
            panelClass: 'confirm-dialog',
            data: this.measurementSelectedWorkareas,
        });
        dialogRef.componentInstance.transferred.subscribe(() => this.reload());
    }

    openMeasurementExportDialog(): void {
        if (!this.projectId) {
            this.confirmDialogService.confirm(
                'Geen project geselecteerd',
                'Selecteer eerst een project in het filter om werkvakken te kunnen exporteren.',
                'Sluiten',
                ''
            );
            return;
        }
        this.subscriptions.add(
            this.customerProjectService.getProjectDetail(+this.projectId).subscribe({
                next: project => {
                    const dialogRef = this.dialog.open(CustomerProjectExportDialogComponent, {
                        maxHeight: '98vh',
                        minWidth: '750px',
                        panelClass: 'confirm-dialog',
                        disableClose: true,
                        data: {
                            project: project.data,
                            customer: this.customer,
                            type: 'Excel',
                            initialWorkareaIds: this.measurementSelectedWorkareas.map(w => w.id),
                        } as ExportDialogData,
                    });
                    this.subscriptions.add(
                        dialogRef.afterClosed().subscribe(result => {
                            if (!result) {
                                return;
                            }
                            const statusIds = result.status
                                ? Object.keys(result.status)
                                      .filter(key => result.status[key])
                                      .map(Number)
                                : undefined;
                            this.exportService
                                .downloadExcelWorkareas(this.projectId, result.workarea_ids, statusIds)
                                .subscribe();
                        })
                    );
                },
                // Zonder customer_project_access geeft getProjectDetail() een 404; dat anders
                // stil laten mislukken verwart de gebruiker.
                error: () => {
                    this.confirmDialogService
                        .confirm(
                            'Geen toegang tot dit project',
                            'Vraag toegang aan via het projectenoverzicht om werkvakken te kunnen exporteren.',
                            'Toegang aanvragen',
                            'Sluiten'
                        )
                        .then(() =>
                            this.router.navigate([
                                this.Routenames.customers,
                                this.customer.id,
                                this.Routenames.projects,
                                this.projectId,
                                this.Routenames.requestAccess,
                            ])
                        )
                        .catch(() => {});
                },
            })
        );
    }

    onSearchInputFocus(): void {
        if ((this.addressAutocomplete.value ?? '').toString().trim()) {
            return;
        }

        this.hectoPins.set([]);
        this.addressResults.set(this.locationService.getDefaultSuggestions());
    }

    public filterToggle() {
        if (this.mobileMenuOpen) {
            this.mobileMenuOpen = false;
        }
        this.filterbarVisible = !this.filterbarVisible;
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
        this.actualZoom = this.googleMap?.getZoom() ?? 0;
        this.hideClusterPolygons = this.actualZoom >= 16;
        this.polygonOptions = { ...this.polygonOptions, fillOpacity: this.actualZoom < 12 ? 0.35 : 0 };
        this.mapBounds = this.googleMap?.getBounds() ?? null;
        this.updateWorkareasInBoundsCount();
        this.updateRenderBounds();

        const boundsChanged$ = this.googleMap.boundsChanged.pipe(shareReplay({ bufferSize: 1, refCount: true }));

        this.subscriptions.add(
            boundsChanged$.pipe(debounceTime(200)).subscribe(() => {
                this.mapBounds = this.googleMap?.getBounds() ?? null;
                this.updateWorkareasInBoundsCount();
            })
        );

        this.subscriptions.add(boundsChanged$.pipe(auditTime(150)).subscribe(() => this.updateRenderBounds()));

        this.subscriptions.add(
            boundsChanged$
                .pipe(
                    debounceTime(500),
                    // Binnen buffer: niet opnieuw ophalen; lopend verzoek behouden.
                    filter(() => {
                        const zoomLevel = this.googleMap?.getZoom() ?? this.actualZoom ?? 0;
                        return !(
                            zoomLevel >= 16 &&
                            this.mapBounds &&
                            this.hectometersBounds &&
                            this.hectometersDetailed === zoomLevel >= 18 &&
                            this.hectometersBounds.contains(this.mapBounds.getSouthWest()) &&
                            this.hectometersBounds.contains(this.mapBounds.getNorthEast())
                        );
                    }),
                    switchMap(() => {
                        const zoomLevel = this.googleMap?.getZoom() ?? this.actualZoom ?? 0;
                        if (zoomLevel < 16) {
                            this.hectometers = [];
                            this.hectometersBounds = null;
                            return EMPTY;
                        }
                        if (!this.mapBounds) {
                            return EMPTY;
                        }

                        // Haal op voor een iets groter gebied zodat je een buffer hebt
                        const southWest = this.mapBounds.getSouthWest();
                        const northEast = this.mapBounds.getNorthEast();
                        const viewSize = this.mapBounds.toSpan();
                        this.hectometersBounds = new google.maps.LatLngBounds(
                            { lat: southWest.lat() - viewSize.lat(), lng: southWest.lng() - viewSize.lng() },
                            { lat: northEast.lat() + viewSize.lat(), lng: northEast.lng() + viewSize.lng() }
                        );
                        // Zoom ≥18: hectometres; otherwise kilometre posts.
                        this.hectometersDetailed = zoomLevel >= 18;
                        return this.hectoService.getHectopins(this.hectometersBounds, zoomLevel);
                    })
                )
                .subscribe({
                    next: hectometers => {
                        this.hectometers = hectometers.data;
                    },
                    error: e => {
                        this.hectometers = [];
                        console.error('Failed to load hectometers for the current map bounds', e);
                    },
                })
        );
    }

    private updateRenderBounds() {
        const bounds = this.googleMap?.getBounds();
        if (!bounds) {
            this.renderBounds = this.renderTriggerBounds = null;
            return;
        }
        const zoom = this.googleMap.getZoom();
        if (
            this.renderTriggerBounds &&
            this.renderZoom === zoom &&
            this.renderTriggerBounds.contains(bounds.getSouthWest()) &&
            this.renderTriggerBounds.contains(bounds.getNorthEast())
        ) {
            return;
        }
        // Halve viewport marge aan elke kant, verversen zodra de helft daarvan is verbruikt
        this.renderBounds = this.expandBounds(bounds, 0.5);
        this.renderTriggerBounds = this.expandBounds(bounds, 0.25);
        this.renderZoom = zoom;
    }

    private expandBounds(bounds: google.maps.LatLngBounds, factor: number) {
        const southWest = bounds.getSouthWest();
        const northEast = bounds.getNorthEast();
        const viewSize = bounds.toSpan();
        return new google.maps.LatLngBounds(
            { lat: southWest.lat() - viewSize.lat() * factor, lng: southWest.lng() - viewSize.lng() * factor },
            { lat: northEast.lat() + viewSize.lat() * factor, lng: northEast.lng() + viewSize.lng() * factor }
        );
    }

    private updateWorkareasInBoundsCount() {
        this.workareasInBoundsCount = this.workareasBoundsCountPipe.transform(this.filteredWorkareas(), this.mapBounds);
    }

    ngOnInit() {
        super.ngOnInit();
        const screenshotRoute = this.route.pathFromRoot.some(
            route => route.snapshot.routeConfig?.path === this.Routenames.customersScreenshot
        );
        const normalMapType =
            this.route.snapshot.queryParams['maptype'] && this.route.snapshot.queryParams['maptype'] === 'normal';
        this.screenshot = screenshotRoute;

        if (this.screenshot && !normalMapType) {
            this.mapTypeId = google.maps.MapTypeId.SATELLITE;
        }

        const search$ = this.addressAutocomplete.valueChanges.pipe(
            map((value: unknown) => (typeof value === 'string' ? value.trim() : '')),
            tap((value: string) => {
                this.loading = !!value;
                this.updateWorkareaSearch(value);
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.addressSearch = merge(
            search$.pipe(
                filter((value: string) => !value),
                tap(() => this.hectoPins.set([])),
                map(() => [])
            ),
            this.createHectoSearchStream(search$),
            this.createAddressSearchStream(search$)
        );

        this.subscriptions.add(
            this.addressSearch.subscribe(results => {
                this.addressResults.set(results ?? []);
                this.loading = false;
            })
        );

        this.workareaService.getAvailableStatuses('general').then(statuses => (this.status = statuses));

        this.subscriptions.add(
            this.route.queryParams.subscribe(params => {
                if (+params['movingWorkarea'] > 0) {
                    this.movingWorkarea = +params['movingWorkarea'];
                }
                if (params['pdf_style']) {
                    this.pdfStyle = params['pdf_style'];
                }
            })
        );

        this.subscriptions.add(
            this.filterRef.filterChange.subscribe(filter => {
                this.filter = filter;
                if (this.screenshot) {
                    this.reload(true);
                } else {
                    this.filterWorkareas(filter);
                }
            })
        );

        this.subscriptions.add(this.filterRef.closeFilter.subscribe(() => (this.filterbarVisible = false)));

        if (!this.screenshot) {
            this.reload(true);
        }

        this.subscriptions.add(this.route.queryParams.subscribe(params => (this.projectId = params['project'])));

        this.customer = LocalStorage.getCustomer();
        this.customerRegion = new Region(this.customer.area);
    }

    moveWorkarea() {
        const route = [this.Routenames.customers, this.customer.id, this.Routenames.workareas, this.movingWorkarea] as (
            | number
            | string
            | object
        )[];
        const params = {
            project: this.activatedRoute.snapshot.queryParams['project'],
            newLat: Settings.mapCenter.lat,
            newLng: Settings.mapCenter.lng,
            movingWorkarea: null,
        };
        this.movingWorkarea = null;
        this.router.navigate(route, {
            queryParams: params,
            queryParamsHandling: 'merge',
        });
    }

    public createWorkarea(isDocument = false) {
        if (!this.customerRegion.insideCustomer(Settings.mapCenter)) {
            if (LocalStorage.getCustomer().area_forced) {
                this.confirmDialogService.confirm(
                    'Melding buiten werkgebied',
                    'Je kunt geen melding buiten het werkgebied aanmaken. ' +
                        'Om een melding te maken moet je je binnen het werkgebied bevinden.',
                    'Oké'
                );
            } else {
                this.confirmDialogService
                    .confirm(
                        'Project buiten gemeente',
                        'Je probeert een project te maken buiten je gemeente, weet je zeker dat je door wilt gaan?',
                        'Ja',
                        'Nee'
                    )
                    .then(() => this.chooseWorkareaType())
                    .catch(() => {});
            }
        } else {
            this.chooseWorkareaType();
        }
    }

    public reload(initialCustomer = false) {
        this.filteredWorkareas.set([]);
        this.workareaService.getAll(true, this.screenshot ? this.filter.statuses : null, true).then(workareas => {
            this.workareas.set(workareas);
            this.cdref.detectChanges();
            this.selectedWorkareas.forEach(w => {
                const nw = this.workareas().find(wa => wa.id === w.id);
                Object.assign(w, nw);
            });
            const filterOnlyType = new Filter();
            if (this.projectId === undefined && this.route.snapshot.queryParams['workareaType'] === undefined) {
                this.filter.workareaType = 'Werkvakken';
            }
            filterOnlyType.workareaType = this.route.snapshot.queryParams['workareaType'];
            this.totalWorkareaCount = filterOnlyType.apply(this.workareas()).length;
            this.filterWorkareas(this.filter);

            if (initialCustomer) {
                if (this.filteredWorkareas()?.length > 1) {
                    const visibleWorkareasRegion = new Region(this.filteredWorkareas());
                    const prevData = LocalStorage.getRestoreData();
                    if (prevData?.customerId === this.customer.id) {
                        this.zoomCenter(prevData.zoom, { lat: prevData.lat, lng: prevData.lng });
                    } else {
                        this.zoomCenter(
                            visibleWorkareasRegion.bestMapView(window.innerWidth, window.innerHeight, 5),
                            visibleWorkareasRegion.centroid()
                        );
                    }
                } else if (this.filteredWorkareas()?.length === 1) {
                    this.scrollToWorkarea(this.filteredWorkareas()[0].id);
                } else {
                    this.centerCustomer();
                }
            }
        });
    }

    public centerCustomer() {
        // A customer without a drawn area has nothing to center on, so show the country instead.
        if (!this.customerRegion?.hasPoints()) {
            this.centerCountry();
            return;
        }
        this.zoomCenter(
            this.customerRegion.bestMapView(window.innerWidth, window.innerHeight, 5),
            this.customerRegion.centroid()
        );
        this.resetHeading();
    }

    public workareaClick(event: google.maps.MapMouseEvent) {
        if (
            this.measuring ||
            (this.actualZoom < 16 && this.workareasInBoundsCount >= this.doNotClusterBelowPointsInView)
        ) {
            return;
        }
        if (event.domEvent) {
            event.domEvent.stopPropagation();
        }
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const clickedPoint = new google.maps.LatLng(lat, lng);

        this.ngZone.run(() => {
            this.isSearching = false;
            this.selectedWorkareas = [];

            /*
                Calculate ratio between all the workareas on the map,
                if workareas are close together show a detail preview box with those workarea details.
            */

            if (Settings.mapZoom > 8) {
                const latitudeRanges = {
                    22: 0.00002,
                    21: 0.00003,
                    20: 0.00003,
                    19: 0.00005,
                    18: 0.00012,
                    17: 0.00025,
                    16: 0.0006,
                    15: 0.0012,
                    14: 0.0025,
                    13: 0.005,
                    12: 0.012,
                    11: 0.024,
                    10: 0.05,
                    9: 0.09,
                };
                const longitudeRanges = {
                    22: 0.00003,
                    21: 0.00004,
                    20: 0.00005,
                    19: 0.00008,
                    18: 0.0002,
                    17: 0.0004,
                    16: 0.001,
                    15: 0.002,
                    14: 0.004,
                    13: 0.008,
                    12: 0.02,
                    11: 0.04,
                    10: 0.08,
                    9: 0.15,
                };

                const latitudeRange = latitudeRanges[Math.round(Settings.mapZoom)];
                const longitudeRange = longitudeRanges[Math.round(Settings.mapZoom)];

                this.filteredWorkareas().forEach(filtereddWorkarea => {
                    let shouldAdd = false;

                    if (
                        filtereddWorkarea.geom_geojson &&
                        filtereddWorkarea.geom_geojson.type === 'Polygon' &&
                        filtereddWorkarea.geom_geojson.coordinates
                    ) {
                        const coordinates = filtereddWorkarea.geom_geojson.coordinates;

                        if (coordinates.length > 0) {
                            const outerRing = coordinates[0].map(([lng, lat]) => new google.maps.LatLng(lat, lng));

                            if (
                                google.maps.geometry.poly.containsLocation(
                                    clickedPoint,
                                    new google.maps.Polygon({ paths: outerRing })
                                )
                            ) {
                                let inHole = false;

                                // Check if point is in any hole (skip first element which is outer ring)
                                for (let i = 1; i < coordinates.length; i++) {
                                    const hole = coordinates[i].map(([lng, lat]) => new google.maps.LatLng(lat, lng));
                                    if (
                                        google.maps.geometry.poly.containsLocation(
                                            clickedPoint,
                                            new google.maps.Polygon({ paths: hole })
                                        )
                                    ) {
                                        inHole = true;
                                        break;
                                    }
                                }

                                if (!inHole) {
                                    shouldAdd = true;
                                }
                            }
                        }
                    }

                    let calculatedLatitudeDifference = filtereddWorkarea.lat - lat;
                    let calculatedLongitudeDifference = filtereddWorkarea.lng - lng;

                    calculatedLatitudeDifference = +Math.abs(calculatedLatitudeDifference).toFixed(5);
                    calculatedLongitudeDifference = +Math.abs(calculatedLongitudeDifference).toFixed(5);

                    if (
                        calculatedLatitudeDifference < latitudeRange &&
                        calculatedLongitudeDifference < longitudeRange
                    ) {
                        shouldAdd = true;
                    }

                    if (shouldAdd) {
                        this.selectedWorkareas.push(filtereddWorkarea);
                    }
                });
            }

            const pixelY = event['pixel']?.y ?? event.domEvent['screenY'];
            const pixelX = event['pixel']?.x ?? event.domEvent['screenX'];

            let top: any = pixelY + 10;
            let left: any = pixelX + 25;
            let right = 'auto';
            let bottom = 'auto';
            const screenHeight = document.body.scrollHeight;
            const mapWidth =
                document.body.scrollWidth -
                document.querySelector('.searchbar').clientWidth -
                document.querySelector('.filterbar').clientWidth;
            const screenWith = document.body.scrollWidth - document.querySelector('.filterbar').clientWidth;

            let maxHeight = 'calc(100% - ' + (pixelY + 10) + 'px)';

            if (top > screenHeight / 2) {
                bottom = (pixelY - screenHeight) * -1 + 20 + 'px';
                top = 'auto';
                maxHeight = pixelY - 20 + 'px';
            } else {
                top = top + 'px';
            }

            if (left > screenWith - 330) {
                let add = document.querySelector('.searchbar').clientWidth;
                if (add && document.querySelector('.filterbar').clientWidth) {
                    add = document.querySelector('.filterbar').clientWidth;
                }

                left = 'auto';
                right = mapWidth + add - pixelX + 25 + 'px';
            } else {
                left = left - document.querySelector('.searchbar').clientWidth + 'px';
            }
            if (mapWidth < 700) {
                left = 'calc(50% - 165px)';
            }

            this.selectedPosition = {
                left: left,
                right: right,
                top: top,
                bottom: bottom,
                maxHeight: maxHeight,
            };
        });
    }

    goHome() {
        if (this.customer.consumer_customer) {
            this.router.navigate([``], { replaceUrl: true });
        } else {
            this.router.navigate([this.Routenames.customers], { replaceUrl: true });
        }
    }

    override centerOrZoomChanged() {
        LocalStorage.setRestoreData(
            +this.customer.id,
            +this.projectId,
            this.googleMap.getZoom(),
            this.googleMap.getCenter().lat(),
            this.googleMap.getCenter().lng()
        );
        this.selectedWorkareas = [];
    }

    override zoomChanged() {
        this.actualZoom = Settings.mapZoom ?? this.googleMap?.getZoom() ?? 0;
        this.hideClusterPolygons = this.actualZoom >= 16;
        this.polygonOptions = { ...this.polygonOptions, fillOpacity: this.actualZoom < 12 ? 0.35 : 0 };
    }

    private chooseWorkareaType() {
        const dialogRef = this.dialog.open(WorkareaProjectChooseDialogComponent, {
            panelClass: 'confirm-dialog',
            data: {},
        });

        const subs = dialogRef.afterClosed().subscribe(type => {
            if (type) {
                const route = [this.Routenames.customers, this.customer.id, this.Routenames.workareas, -1] as (
                    | number
                    | string
                    | object
                )[];
                const params = { project: this.activatedRoute.snapshot.queryParams['project'] };
                if (type === this.Routenames.documents) {
                    route.push(this.Routenames.documents);
                }

                params['type'] = type;
                this.router.navigate(route, {
                    queryParams: params,
                    queryParamsHandling: 'merge',
                });
            }
            subs.unsubscribe();
        });
    }

    private filterWorkareas(filter: Filter) {
        this.appliedAnyFilter = false;
        if (this.workareas()) {
            this.filteredWorkareas.set(filter.apply(this.workareas()));

            this.filteredWorkareaCount = this.filteredWorkareas().length;
            this.updateWorkareasInBoundsCount();
            this.scrollToWorkarea();
            if (this.screenshot) {
                this.centerWorkareas();
            }
        }
    }

    allWorkareasVisualLoaded() {
        const allImages = document.querySelectorAll('.gm-style img');
        const foundWorkareas = Array.from(allImages).filter(img => {
            const src = (img as HTMLImageElement).src || '';
            try {
                const decoded = decodeURIComponent(src);
                return decoded.includes('data-type="workarea"') || decoded.includes("data-type='pushpin'");
            } catch (e) {
                return false;
            }
        });
        return this.filteredWorkareas()?.length === foundWorkareas?.length;
    }

    async centerWorkareas() {
        const workareas = this.filteredWorkareas() ?? [];
        const maxWait = Math.min(60000, Math.max(2500, workareas.length * 60));

        const visualLoaded = await this.waitUntil(() => this.allWorkareasVisualLoaded(), maxWait, 200);

        if (!visualLoaded) {
            this.signalReadyForPuppeteer();
            return;
        }

        // Eerst de map-mutatie uitvoeren.
        if (workareas.length === 1) {
            this.zoomCenter(18, workareas[0]);
        } else if (workareas.length > 1) {
            const visibleWorkareasRegion = new Region(workareas);
            this.zoomCenter(
                visibleWorkareasRegion.bestMapView(window.innerWidth, window.innerHeight, 5),
                visibleWorkareasRegion.centroid()
            );
        }

        // Daarna wachten op de idle die volgt op deze zoom/center actie.
        await this.waitForMapIdle();

        // Wacht op afbeeldingen/icons.
        await this.waitForImages();

        // Geef Chromium nog tijd voor compositing.
        await this.nextFrame();
        await this.nextFrame();

        setTimeout(() => this.signalReadyForPuppeteer(), 3000);
    }

    private waitUntil(predicate: () => boolean, timeoutMs: number, intervalMs: number): Promise<boolean> {
        const startTime = Date.now();

        return new Promise(resolve => {
            const check = () => {
                if (predicate()) {
                    resolve(true);
                    return;
                }

                if (Date.now() - startTime >= timeoutMs) {
                    resolve(false);
                    return;
                }

                setTimeout(check, intervalMs);
            };

            check();
        });
    }

    private waitForMapIdle(): Promise<void> {
        return new Promise(resolve => {
            const subscription = this.googleMap.idle.subscribe(() => {
                subscription.unsubscribe();
                resolve();
            });
        });
    }

    private async waitForImages(): Promise<void> {
        const images = Array.from(document.images);

        await Promise.all(
            images.map(async img => {
                try {
                    if (!img.complete) {
                        await new Promise<void>(resolve => {
                            img.onload = () => resolve();
                            img.onerror = () => resolve();
                        });
                    }

                    if (img.decode) {
                        await img.decode();
                    }
                } catch {
                    // Niet blokkeren op kapotte externe tiles/icons.
                }
            })
        );
    }

    private nextFrame(): Promise<void> {
        return new Promise(resolve => requestAnimationFrame(() => resolve()));
    }

    private signalReadyForPuppeteer(): void {
        if (!document.querySelector('readyforpuppeteer')) {
            document.body.appendChild(document.createElement('readyforpuppeteer'));
        }
    }

    private scrollToWorkarea(workareaId?: number) {
        workareaId = workareaId ?? this.scrollToWorkareaId;
        if (workareaId) {
            const workarea = this.workareas().find(wa => wa.id === workareaId);
            if (workarea) {
                this.setMapCenterAndZoom({
                    lat: +workarea.lat,
                    lng: +workarea.lng,
                });

                if (window.innerWidth <= 767) {
                    this.searchbarVisible = false;
                }
            } else {
                this.scrollToWorkareaId = workareaId;
            }
        }
    }

    private setMapCenterAndZoom(param: { lat: number; lng: number }) {
        this.zoomCenter(18, param);
    }

    copyGoogleMapsLink() {
        const center = this.googleMap.getCenter();
        const zoom = this.googleMap.getZoom();
        const link = `https://www.google.com/maps?q=${center.lat()},${center.lng()}`;

        navigator.clipboard.writeText(link);

        this.closeMenu();
    }

    openContextMenu(event: google.maps.MapMouseEvent | MouseEvent, template: TemplateRef<unknown>) {
        // close if already open
        this.closeMenu();

        // get cursor coords (Google map gives domEvent)
        const mouseEvent = 'domEvent' in event ? (event.domEvent as MouseEvent) : (event as MouseEvent);
        const clientX = mouseEvent.clientX;
        const clientY = mouseEvent.clientY;

        // create overlay strategy
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo({ x: clientX, y: clientY })
            .withPositions([{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' }]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
        });

        // attach the ng-template
        this.overlayRef.attach(new TemplatePortal(template, this.vcr));

        // close when clicking outside
        this.overlayRef.backdropClick().subscribe(() => this.closeMenu());
    }

    goToHectometre(hecto) {
        this.hectometers = [hecto];
        // Clear bounds to reload after showing this single hectometre.
        this.hectometersBounds = null;
        this.setMapCenterAndZoom({ lat: +hecto.lat, lng: +hecto.lng });
        this.closeMenu();
    }

    private closeMenu() {
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    ngOnDestroy() {
        this.closeMenu();
        super.ngOnDestroy();
        LocalStorage.resetRestoreData();
    }
}
